import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { BiTransferAlt } from 'react-icons/bi';
import { TiDeleteOutline } from 'react-icons/ti';
import { CgAdd } from 'react-icons/cg';
import ShowModal from '../utils/ShowModal';
import InputField from './inputFields/InputField';
import TextareaField from './inputFields/TextField';
import FormikDropdown from './inputFields/FormikDropDown';
import ImagePicker from './inputFields/ImagePicker';
import { convertProformaToOrder } from '../services/labelInvoice';

const PAYMENT_MODE_OPTIONS = [
    { label: 'UPI', value: 'UPI' },
    { label: 'CARD', value: 'CARD' },
    { label: 'CASH', value: 'CASH' },
    { label: 'CHEQUE', value: 'CHEQUE' },
];

const PAYMENT_STATUS_OPTIONS = [
    { label: 'Pending', value: 'PENDING' },
    { label: 'Completed', value: 'COMPLETED' },
];

const emptyPayment = () => ({
    payment_mode: '',
    payment_amount: '',
    payment_status: 'COMPLETED',
    payment_id: '',
    imageFile: null,
});

const initialValues = {
    delivery_note: '',
    transport_name: '',
    zipcode: '',
    payments: [],
};

const validationSchema = Yup.object({
    delivery_note: Yup.string().trim().required('Delivery note is required'),
});

/**
 * proforma: { id, isConverted?, orderId?, orderStatus? } - `isConverted` (from the
 * proforma's is_converted_to_order flag) hides the button once it has been turned
 * into an order; orderId/orderStatus are legacy hints kept for backwards compat.
 * The API remains the source of truth for whether a conversion is actually allowed.
 */
const ConvertProformaToOrderButton = ({ proforma, onSuccess, className = '' }) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);

    const isFinal = Boolean(proforma?.isConverted)
        || Boolean(proforma?.orderId && proforma?.orderStatus === 'CONFIRMED');
    if (isFinal) return null;

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const res = await convertProformaToOrder({
                proformaId: proforma.id,
                deliveryNote: values.delivery_note || undefined,
                transportName: values.transport_name || undefined,
                zipcode: values.zipcode || undefined,
                payments: values.payments,
            });
            const { order_id, message } = res.data;
            toast.success(message || 'Proforma converted to order.');
            setIsOpen(false);
            if (onSuccess) {
                onSuccess(res.data);
            } else if (order_id) {
                navigate(`/orders/${order_id}`);
            }
        } catch (err) {
            const data = err?.response?.data;
            if (data?.unmatched_products?.length) {
                toast.error(
                    <div>
                        <p>{data.error}</p>
                        <p className="mt-1">Could not match these products to inventory:</p>
                        <ul className="list-disc pl-4 mt-1">
                            {data.unmatched_products.map((name) => (
                                <li key={name}>{name}</li>
                            ))}
                        </ul>
                        <p className="mt-1">Fix the proforma item names and try again.</p>
                    </div>,
                    { autoClose: false }
                );
            } else {
                toast.error(data?.error || err?.message || 'Failed to convert proforma to order.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className={`flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-[11px] font-bold hover:bg-primary-dark transition-all shadow-sm ${className}`}
            >
                <BiTransferAlt />
                Convert to Order
            </button>

            <ShowModal
                isOpen={isOpen}
                onRequestClose={() => setIsOpen(false)}
                title="Convert to Order"
                modalWidth="640px"
                componentToPass={
                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                        {({ values, setFieldValue, isSubmitting }) => (
                            <Form className="space-y-4">
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                    This will create/confirm a real B2B order from this proforma invoice and cannot be trivially undone.
                                </p>

                                <TextareaField label="Delivery Note" name="delivery_note" placeholder="Delivery note for this order" required />

                                <div className="grid grid-cols-2 gap-3">
                                    <InputField label="Transport Name" name="transport_name" placeholder="Transport name" />
                                    <InputField label="Zipcode" name="zipcode" placeholder="Zipcode" />
                                </div>

                                <FieldArray name="payments">
                                    {({ push, remove }) => (
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-sm font-medium text-gray-700">Payments (optional)</p>
                                                <button
                                                    type="button"
                                                    onClick={() => push(emptyPayment())}
                                                    className="flex items-center gap-1 text-xs font-bold text-primary hover:underline"
                                                >
                                                    <CgAdd /> Add Payment
                                                </button>
                                            </div>
                                            <div className="space-y-3">
                                                {values.payments.map((payment, index) => (
                                                    <div key={index} className="border border-gray-200 rounded-lg p-3 relative">
                                                        <button
                                                            type="button"
                                                            onClick={() => remove(index)}
                                                            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                                                        >
                                                            <TiDeleteOutline size={20} />
                                                        </button>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pr-6">
                                                            <InputField
                                                                label="Amount"
                                                                name={`payments.${index}.payment_amount`}
                                                                placeholder="₹ Amount"
                                                            />
                                                            <FormikDropdown
                                                                label="Mode"
                                                                name={`payments.${index}.payment_mode`}
                                                                placeholder="Select Mode"
                                                                options={PAYMENT_MODE_OPTIONS}
                                                            />
                                                            <FormikDropdown
                                                                label="Status"
                                                                name={`payments.${index}.payment_status`}
                                                                placeholder="Select Status"
                                                                options={PAYMENT_STATUS_OPTIONS}
                                                            />
                                                            <InputField
                                                                label="Txn. ID"
                                                                name={`payments.${index}.payment_id`}
                                                                placeholder="Transaction ID"
                                                            />
                                                        </div>
                                                        <div className="h-28 mt-3">
                                                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Payment Proof</p>
                                                            <ImagePicker
                                                                preloadedImage={payment.imageFile}
                                                                placeholder="Select Payment Proof"
                                                                onImageSelect={(file) => setFieldValue(`payments.${index}.imageFile`, file)}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </FieldArray>

                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsOpen(false)}
                                        className="px-4 py-2 text-xs font-bold text-gray-600 border border-gray-200 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-5 py-2 text-xs font-black bg-primary text-white rounded-lg disabled:bg-gray-300"
                                    >
                                        {isSubmitting ? 'Converting...' : 'Convert to Order'}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                }
            />
        </>
    );
};

export default ConvertProformaToOrderButton;
